import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Базовый sliding-up modal с backdrop и Portal. Block 12 PR-B Phase B1.
 *
 * Используется напрямую (children любые) и как фундамент для специализированных
 * sheet'ов (PickerSheet — список options; будущие — выбор бренда декора,
 * выбор столешницы в Phase B3+ AnketaForm).
 *
 * Поведение:
 * - `isOpen=false` → возвращает `null` (не в DOM). Никаких CSS-анимаций входа/выхода
 *   в B1 — анимации откладываются в backlog (см. B-NNN при появлении необходимости).
 * - `isOpen=true` → Portal в `document.body`. Backdrop затемнённый. Sheet
 *   снизу с `max-h-[80vh]` (override через `maxHeightVh`).
 * - Escape key → onClose (effect listener на window).
 * - Backdrop click → onClose.
 * - Close button (X в правом верхнем углу) → onClose.
 * - Body scroll lock пока sheet открыт (`document.body.style.overflow='hidden'`),
 *   restore при unmount или `isOpen=false` next render.
 *
 * НЕ реализовано в B1 (backlog):
 * - Swipe-to-close gesture.
 * - Focus trap (focus-trap-react / inert).
 * - Animated open/close transition.
 *
 * P-022 a11y:
 * - `role="dialog" aria-modal="true" aria-labelledby={titleId}`.
 * - Close button `aria-label="Закрыть"`, `min-w-tap min-h-tap`.
 * - focus-visible:ring-2 focus-visible:ring-cta.
 */
export interface BottomSheetProps {
  /** Контролируемое состояние открытия. */
  isOpen: boolean;
  /** Title в header sheet (Manrope Variable — font-display per ADR-0023). */
  title: string;
  /** Callback на закрытие (backdrop click, Escape, close button). */
  onClose: () => void;
  /** Body content. */
  children: ReactNode;
  /** Optional override max-height в vh. Default 80. */
  maxHeightVh?: number;
  /** Optional ARIA label для dialog. По умолчанию title через aria-labelledby. */
  'aria-label'?: string;
}

export function BottomSheet({
  isOpen,
  title,
  onClose,
  children,
  maxHeightVh = 80,
  'aria-label': ariaLabel,
}: BottomSheetProps) {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="bottomsheet-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel ? undefined : titleId}
        aria-label={ariaLabel}
        style={{ maxHeight: `${maxHeightVh}vh` }}
        className="relative w-full bg-card rounded-t-3xl shadow-card flex flex-col"
      >
        <div aria-hidden="true" className="mx-auto mt-2 w-12 h-1 rounded-full bg-muted/40" />
        <h2 id={titleId} className="font-display text-h2 text-ink text-center mt-2 px-12">
          {title}
        </h2>
        <button
          type="button"
          aria-label="Закрыть"
          onClick={onClose}
          className="absolute right-2 top-2 min-w-tap min-h-tap flex items-center justify-center rounded-full text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
        >
          <X size={22} aria-hidden="true" />
        </button>
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
